import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminSocials.css';
import { motion } from 'framer-motion';
import { FaFacebook, FaTwitter, FaLinkedin, FaSave, FaExternalLinkAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminSocials = () => {
    const [socials, setSocials] = useState({
        facebook: '',
        twitter: '',
        linkedin: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchSocialLinks();
    }, []);

    const fetchSocialLinks = async () => {
        setIsLoading(true);
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/social-links', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });
            if (ok && result.data) {
                setSocials({
                    facebook: result.data.facebook || '',
                    twitter: result.data.twitter || '',
                    linkedin: result.data.linkedin || ''
                });
            }
        } catch (error) {
            console.error('Error fetching social links:', error);
            toast.error('Failed to load social links');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const { ok, data: errorData } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/social-links', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(socials)
            });

            if (ok) {
                toast.success('Social links updated successfully!');
            } else {
                toast.error(errorData.message || 'Failed to update social links');
            }
        } catch (error) {
            console.error('Error saving social links:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-layout">
                <Sidebar />
                <main className="admin-content">
                    <div className="admin-loading-container" style={{ textAlign: 'center', padding: '100px' }}>
                        <div className="admin-loader"></div>
                        <p style={{ marginTop: '20px', color: '#64748b' }}>Loading social links...</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Social <span>Links</span></h1>
                        <p>Manage your brand's digital presence across platforms.</p>
                    </div>
                </header>

                <div className="socials-container">
                    <motion.form
                        className="socials-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        onSubmit={handleSave}
                    >
                        <div className="social-input-group">
                            <div className="platform-info">
                                <div className="platform-icon fb">
                                    <FaFacebook />
                                </div>
                                <div className="platform-text">
                                    <h3>Facebook</h3>
                                    <span>Profile or Business Page URL</span>
                                </div>
                            </div>
                            <div className="input-wrapper">
                                <input
                                    type="url"
                                    value={socials.facebook}
                                    onChange={(e) => setSocials({ ...socials, facebook: e.target.value })}
                                    placeholder="https://facebook.com/yourpage"
                                />
                                {socials.facebook && (
                                    <a href={socials.facebook} target="_blank" rel="noopener noreferrer" className="verify-link">
                                        <FaExternalLinkAlt />
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="social-input-group">
                            <div className="platform-info">
                                <div className="platform-icon tw">
                                    <FaTwitter />
                                </div>
                                <div className="platform-text">
                                    <h3>Twitter / X</h3>
                                    <span>Account or Handle URL</span>
                                </div>
                            </div>
                            <div className="input-wrapper">
                                <input
                                    type="url"
                                    value={socials.twitter}
                                    onChange={(e) => setSocials({ ...socials, twitter: e.target.value })}
                                    placeholder="https://twitter.com/yourhandle"
                                />
                                {socials.twitter && (
                                    <a href={socials.twitter} target="_blank" rel="noopener noreferrer" className="verify-link">
                                        <FaExternalLinkAlt />
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="social-input-group">
                            <div className="platform-info">
                                <div className="platform-icon li">
                                    <FaLinkedin />
                                </div>
                                <div className="platform-text">
                                    <h3>LinkedIn</h3>
                                    <span>Company or Individual Profile URL</span>
                                </div>
                            </div>
                            <div className="input-wrapper">
                                <input
                                    type="url"
                                    value={socials.linkedin}
                                    onChange={(e) => setSocials({ ...socials, linkedin: e.target.value })}
                                    placeholder="https://linkedin.com/in/yourprofile"
                                />
                                {socials.linkedin && (
                                    <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" className="verify-link">
                                        <FaExternalLinkAlt />
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="socials-footer">
                            <button type="submit" className="save-socials-btn" disabled={isSaving}>
                                {isSaving ? 'Saving Changes...' : <><FaSave /> Save Social Profiles</>}
                            </button>
                        </div>
                    </motion.form>

                    <div className="social-preview-tip">
                        <div className="tip-icon">💡</div>
                        <p>These links are used in the website footer and contact sections. Ensure you include the full <strong>https://</strong> prefix for them to work correctly.</p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminSocials;
